import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';

const REVENUECAT_API_KEY = process.env.REACT_APP_REVENUECAT_API_KEY;
const ENTITLEMENT_ID = 'ilik_plus';
const PRODUCT_ID = 'ilik_plus_monthly';

let isConfiguredFlag = false;

const isNative = () => Capacitor.isNativePlatform();

// Races a promise against a timeout so a hung native call never blocks forever.
const withTimeout = (promise, ms, label) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
  ]);
};

export const initRevenueCat = async () => {
  try {
    const native = isNative();
    if (!native) return;
    if (isConfiguredFlag) return;
    try {
      await withTimeout(Purchases.configure({ apiKey: REVENUECAT_API_KEY }), 10000, 'RevenueCat configure()');
      isConfiguredFlag = true;
    } catch (err) {
      console.error('[RevenueCat] configure error/timeout:', err?.message, '|', err?.name, '|', err?.toString());
    }
  } catch (err) {
    console.error('[RevenueCat] initRevenueCat: UNCAUGHT ERROR —', err?.message, '|', err?.name, '|', err?.toString());
  }
};

// Guarantees configure() has run before any purchase-related call.
// Safe to call repeatedly — will not double-configure.
// Times out after 12s so a hung initRevenueCat() can never hang the caller forever.
export const ensureConfigured = async () => {
  if (isConfiguredFlag) return true;
  try {
    await withTimeout(initRevenueCat(), 12000, 'ensureConfigured (initRevenueCat)');
  } catch (err) {
    console.error('[RevenueCat] ensureConfigured: timed out or errored —', err?.message, '|', err?.name, '|', err?.toString());
    return false;
  }
  return isConfiguredFlag;
};

export const checkPremiumStatus = async () => {
  if (!isNative()) return null;
  await ensureConfigured();
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return !!customerInfo.entitlements.active[ENTITLEMENT_ID];
  } catch {
    return null;
  }
};

export const purchaseMonthly = async () => {
  const native = isNative();
  if (!native) return null;
  await ensureConfigured();
  try {
    const offeringsResult = await withTimeout(Purchases.getOfferings(), 10000, 'RevenueCat getOfferings()');
    // getOfferings() resolves directly to { all, current } — not wrapped in an `offerings` property.
    // Prefer current; fall back to all.default, then the first offering in all, if current is absent.
    const offering = offeringsResult?.current
      || offeringsResult?.all?.default
      || Object.values(offeringsResult?.all || {})[0]
      || null;
    if (offering && offering.availablePackages && offering.availablePackages.length > 0) {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: offering.availablePackages[0] });
      return customerInfo;
    }
    const { customerInfo } = await Purchases.purchaseProduct({ productIdentifier: PRODUCT_ID });
    return customerInfo;
  } catch (err) {
    console.error('[RevenueCat] purchaseMonthly error:', err?.message, '|', err?.name, '|', err?.toString());
    if (err.userCancelled) return null;
    throw err;
  }
};

export const restorePurchases = async () => {
  if (!isNative()) return null;
  await ensureConfigured();
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    return {
      isPremium: !!customerInfo.entitlements.active[ENTITLEMENT_ID],
      customerInfo,
    };
  } catch (err) {
    throw err;
  }
};

// Logs the current user out of RevenueCat (creates a new anonymous user on
// the SDK side). Native-gated and guarded — never throws, e.g. on web where
// RevenueCat is never configured.
export const logOutRevenueCat = async () => {
  if (!isNative()) return;
  try {
    if (!isConfiguredFlag) return;
    await Purchases.logOut();
  } catch (err) {
    console.error('[RevenueCat] logOutRevenueCat error:', err?.message, '|', err?.name, '|', err?.toString());
  }
};

export { isNative };
